import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Thomson's: one-tap add-ons in the cart drawer and cart page.
 * Adds via the AJAX cart and dispatches the same CartLinesUpdateEvent the theme's
 * product form uses, so the drawer, cart page and cart icon all re-render natively.
 */
class CartAddonsComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-addons__form')) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '');
    if (!variantId) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    // Step 272: "Switch to 1kg" swaps one unit of the 250g bag for the 1kg.
    // Step 331: the 1kg is added first; the 250g is only reduced once that add has worked,
    // so a failed add (sold out, quantity rule) never leaves the customer without their bag.
    const swapKey = form.dataset.swapKey || '';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());

      if (response.status) {
        const cart = await this.#fetchCart();
        this.dispatchEvent(
          new CartErrorEvent({ error: response.description || response.message || 'Add to cart failed', code: 'INVALID' })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'cart-addons', itemCount: 0 },
        });
        this.#showError(form, response.description || response.message);
        return;
      }

      let sections = response.sections;
      if (swapKey) {
        try {
          const change = await fetch(`${Theme.routes.cart_change_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: swapKey,
              quantity: Number(form.dataset.swapQty || 0),
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });
          if (change.ok) {
            const changed = await change.json();
            if (changed && changed.sections) sections = changed.sections;
          }
        } catch (e) {
          // The 1kg is in the basket; if the 250g couldn't be reduced the customer can remove it themselves.
        }
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-addons',
          itemCount: 1,
          sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#showError(form);
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @param {string} [message]
   */
  #showError(form, message) {
    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
    let error = form.querySelector('.cart-addons__error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'cart-addons__error';
      error.setAttribute('role', 'alert');
      form.append(error);
    }
    error.textContent = message || 'Sorry, that could not be added. Please try again.';
  }
}

if (!customElements.get('cart-addons-component')) {
  customElements.define('cart-addons-component', CartAddonsComponent);
}
